import { useCallback, useEffect, useRef, useState } from 'react';

import { getPredictionHistoryAction } from '@/server/actions/user/getPredictionHistory.action';
import { I_PredictionHistoryResponse } from '@/server/actions/user/getPredictionHistory.action';
import { C_PREDICTIONS_PER_PAGE_COUNT } from '@/ts/constants/C_PREDICTIONS_PER_PAGE_COUNT';
import { T_UserPredictionEventParticipation } from '@/ts/types/T_UserBlockchainData';

interface I_UsePredictionHistoryProps {
  tokens?: string; // Comma-separated tokens: "BTC,ETH,SOL"
  outcome?: 'WON' | 'LOST' | 'PENDING';
  orderBy?: 'lastSubmissionTime';
  orderDirection?: 'asc' | 'desc';
  initialData?: I_PredictionHistoryResponse | null;
  canceled?: boolean;
}

export const usePredictionHistory = ({
  tokens,
  outcome,
  orderBy = 'lastSubmissionTime',
  orderDirection = 'desc',
  initialData,
  canceled
}: I_UsePredictionHistoryProps = {}) => {
  const [data, setData] = useState<T_UserPredictionEventParticipation[]>(
    initialData?.events || []
  );
  const [total, setTotal] = useState(initialData?.total || 0);
  const [hasMore, setHasMore] = useState(initialData?.hasMore || false);
  const [isLoading, setIsLoading] = useState(false);

  // Tracks the latest request to ignore stale async responses
  const requestIdRef = useRef(0);
  const hasLoadedMoreRef = useRef(false);

  // Refs to avoid stale closure when handleLoadMore is invoked (fixes stage+fr click-with-no-result)
  const dataRef = useRef(data);
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  dataRef.current = data;
  hasMoreRef.current = hasMore;
  isLoadingRef.current = isLoading;

  const fetchData = useCallback(
    async (offset: number = 0, reset: boolean = true) => {
      // Capture current request ID to detect if a newer request supersedes this one
      const myRequestId = ++requestIdRef.current;

      setIsLoading(true);
      try {
        const response = await getPredictionHistoryAction({
          limit: C_PREDICTIONS_PER_PAGE_COUNT,
          offset,
          tokens,
          outcome,
          orderBy,
          orderDirection,
          canceled
        });

        // Ignore response if a newer request has been started
        if (myRequestId !== requestIdRef.current) return;

        if (response?.data) {
          if (reset) {
            hasLoadedMoreRef.current = false;
            setData(response.data.events || []);
          } else {
            hasLoadedMoreRef.current = true;
            setData(prev => [...prev, ...(response.data.events || [])]);
          }
          setTotal(response.data.total || 0);
          setHasMore(response.data.hasMore || false);
        }
      } catch (error) {
        if (myRequestId !== requestIdRef.current) return;
        console.error('Failed to fetch prediction history:', error);
        if (reset) {
          hasLoadedMoreRef.current = false;
          setData([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (myRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [tokens, outcome, orderBy, orderDirection, canceled]
  );

  useEffect(() => {
    const shouldUseInitialData =
      initialData &&
      tokens === undefined &&
      outcome === undefined &&
      orderDirection === 'desc' &&
      canceled === undefined;

    if (shouldUseInitialData && !hasLoadedMoreRef.current) {
      // Increment requestIdRef to cancel any in-flight fetch that used stale
      // filter params (e.g. from Router Cache state restoration). Without this,
      // the stale response would pass the requestId check and overwrite the
      // correct SSR initial data with previously-filtered results.
      ++requestIdRef.current;
      setData(initialData.events || []);
      setTotal(initialData.total || 0);
      setHasMore(initialData.hasMore || false);
      setIsLoading(false);
    } else if (!shouldUseInitialData) {
      // Fetch new data when filters or sorting change
      // fetchData increments requestIdRef internally, so stale responses are ignored
      fetchData(0, true);
    }
  }, [fetchData, tokens, outcome, orderDirection, initialData, canceled]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;
    await fetchData(dataRef.current.length, false);
  }, [fetchData]);

  return {
    data,
    total,
    hasMore,
    isLoading,
    handleLoadMore,
    refetch: () => fetchData(0, true)
  };
};
